import type { Category, SubCategory } from '@features/products/graphql/types';
import type { TenantShop } from '@core/tenant/types';
import { getImageBaseForTenantShop } from '@core/config/shops';
import { checkingImageUrl } from '@shared/utils/imageUrlCheck';
import { scopeCategoryId } from '@core/utils/scopedCategory';

function normalizeCategoryNameKey(name: string | undefined): string {
  return (name ?? '').trim().toLowerCase();
}

export type ShopCategoryRow = {
  shopId: string;
  localCategoryId: string;
  category: Category;
};

/** Dedupe subcategory rows by display name; keep first id, fill missing images. */
export function mergeSubCategoryLists(a: SubCategory[], b: SubCategory[]): SubCategory[] {
  const result: SubCategory[] = [];
  const seen = new Set<string>();
  const pushUnique = (subs: SubCategory[]) => {
    for (const s of subs) {
      const k = normalizeCategoryNameKey(s.sub_name);
      if (!k) continue;
      if (seen.has(k)) {
        const idx = result.findIndex(
          (r) => normalizeCategoryNameKey(r.sub_name) === k
        );
        if (idx !== -1 && !result[idx].image && s.image) {
          result[idx] = { ...result[idx], image: s.image };
        }
        continue;
      }
      seen.add(k);
      result.push({ ...s });
    }
  };
  pushUnique(a);
  pushUnique(b);
  return result;
}

function normalizeSubsForShop(
  cat: Category,
  shopId: string,
  allShops: TenantShop[]
): SubCategory[] {
  const raw = cat.mst_sub_category;
  const arr = Array.isArray(raw) ? raw : raw ? [raw as SubCategory] : [];
  const base = getImageBaseForTenantShop(shopId, allShops);
  return arr.map((sub) => ({
    ...sub,
    id: sub.id ? scopeCategoryId(shopId, sub.id) : sub.id,
    image: checkingImageUrl(sub.image, base),
  }));
}

function mergeSubCategoriesFromGroup(
  group: ShopCategoryRow[],
  allShops: TenantShop[]
): SubCategory[] {
  return group.reduce<SubCategory[]>(
    (acc, row) =>
      mergeSubCategoryLists(
        acc,
        normalizeSubsForShop(row.category, row.shopId, allShops)
      ),
    []
  );
}

/** Tab id prefix — not a UUID so `parseCategorySelection` treats it as local unless we branch. */
export const MERGED_CATEGORY_PREFIX = 'mergecat:';

export function mergedCategoryTabId(normalizedNameKey: string): string {
  const key = normalizedNameKey.trim().toLowerCase();
  if (typeof globalThis.btoa === 'function') {
    const b = globalThis
      .btoa(unescape(encodeURIComponent(key)))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
    return `${MERGED_CATEGORY_PREFIX}${b}`;
  }
  return `${MERGED_CATEGORY_PREFIX}${encodeURIComponent(key)}`;
}

export function isMergedCategoryTabId(id: string | null | undefined): boolean {
  return !!id && id.startsWith(MERGED_CATEGORY_PREFIX);
}

/**
 * One tab per distinct **display name** (case-insensitive). Aggregates every
 * `(shopId, localCategoryId)` pair so product queries can fan out per shop.
 */
export function mergeCategoriesByDisplayName(
  rows: ShopCategoryRow[],
  allShops: TenantShop[]
): {
  tabs: Category[];
  sourcesByTabId: Record<string, { shopId: string; localCategoryId: string }[]>;
} {
  const groups = new Map<string, ShopCategoryRow[]>();
  for (const row of rows) {
    const label = (row.category.category || '').trim();
    if (!label || !row.localCategoryId) continue;
    const k = label.toLowerCase();
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k)!.push(row);
  }

  const tabs: Category[] = [];
  const sourcesByTabId: Record<
    string,
    { shopId: string; localCategoryId: string }[]
  > = {};

  for (const [, group] of groups) {
    if (!group.length) continue;
    const displayLabel = group[0].category.category || '';
    const mergedId = mergedCategoryTabId(displayLabel.toLowerCase());
    const shopOrder = new Map(allShops.map((s, i) => [s.id, i]));
    sourcesByTabId[mergedId] = group
      .map((g) => ({
        shopId: g.shopId,
        localCategoryId: g.localCategoryId,
      }))
      .sort(
        (a, b) =>
          (shopOrder.get(a.shopId) ?? 999) - (shopOrder.get(b.shopId) ?? 999)
      );

    const pick = group.reduce((best, cur) => {
      if (cur.category.image && !best.category.image) return cur;
      return best;
    }, group[0]);

    const base = getImageBaseForTenantShop(pick.shopId, allShops);
    tabs.push({
      ...pick.category,
      id: mergedId,
      category: displayLabel,
      image: checkingImageUrl(pick.category.image, base),
      mst_sub_category: mergeSubCategoriesFromGroup(group, allShops),
    });
  }

  tabs.sort((a, b) =>
    (a.category || '').localeCompare(b.category || '', undefined, {
      sensitivity: 'base',
    })
  );
  return { tabs, sourcesByTabId };
}

/**
 * Dedupe catalog / filter sidebar rows when each shop returns the same category name
 * (decorated categories with composite ids, e.g. from useGraphQLCategories).
 */
export function mergeCatalogCategoriesByName(categories: Category[]): Category[] {
  const result: Category[] = [];
  const indexByName = new Map<string, number>();

  for (const c of categories) {
    const nameKey = normalizeCategoryNameKey(c.category);
    if (!nameKey) continue;

    const subs = Array.isArray(c.mst_sub_category)
      ? c.mst_sub_category
      : c.mst_sub_category
        ? [c.mst_sub_category as SubCategory]
        : [];
    const prevIdx = indexByName.get(nameKey);

    if (prevIdx === undefined) {
      indexByName.set(nameKey, result.length);
      result.push({
        ...c,
        mst_sub_category: subs,
        mergedCategoryIds: c.id ? [c.id] : undefined,
      });
      continue;
    }

    const existing = result[prevIdx]!;
    const existingSubs = Array.isArray(existing.mst_sub_category)
      ? existing.mst_sub_category
      : existing.mst_sub_category
        ? [existing.mst_sub_category as SubCategory]
        : [];

    const priorIds =
      existing.mergedCategoryIds ?? (existing.id ? [existing.id] : []);
    const mergedIds = [...priorIds, ...(c.id ? [c.id] : [])];

    result[prevIdx] = {
      ...existing,
      image: existing.image ?? c.image,
      mst_sub_category: mergeSubCategoryLists(existingSubs, subs),
      mergedCategoryIds: mergedIds.length ? mergedIds : undefined,
      id: existing.id ?? c.id,
    };
  }

  return result;
}
